import { useEffect, useState } from 'react';
import { X, Mail, Check, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SendingProgressModal = ({ 
  isOpen, 
  onClose, 
  jobId, 
  totalRecipients = 0,
  emailType = 'email'
}) => {
  const [progress, setProgress] = useState({
    sent: 0,
    failed: 0,
    total: totalRecipients,
    status: 'in_progress',
    message: 'Starting...'
  });
  const [pollingInterval, setPollingInterval] = useState(null);

  useEffect(() => {
    if (isOpen && jobId) {
      // Start polling for progress
      const interval = setInterval(async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API}/email/progress/${jobId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          setProgress({
            sent: response.data.sent || 0,
            failed: response.data.failed || 0,
            total: response.data.total || totalRecipients,
            status: response.data.status || 'in_progress',
            message: response.data.message || 'Processing...'
          });

          // Stop polling if completed
          if (response.data.status === 'completed') {
            clearInterval(interval);
            setPollingInterval(null);
          }
        } catch (error) {
          console.error('Error fetching progress:', error);
        }
      }, 500); // Poll every 500ms

      setPollingInterval(interval);

      return () => {
        if (interval) clearInterval(interval);
      };
    }
  }, [isOpen, jobId, totalRecipients]);

  // Clean up on close
  useEffect(() => {
    if (!isOpen && pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  }, [isOpen, pollingInterval]);

  if (!isOpen) return null;

  const percentage = progress.total > 0 
    ? Math.round(((progress.sent + progress.failed) / progress.total) * 100) 
    : 0;

  // Consider completed if status is completed OR if percentage is 100
  const isCompleted = progress.status === 'completed' || percentage >= 100;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            {isCompleted ? (
              <Check className="text-green-600" size={24} />
            ) : (
              <Loader2 className="text-purple-600 animate-spin" size={24} />
            )}
            <h2 className="text-lg font-semibold">
              {isCompleted ? 'Sending Complete!' : `Sending ${emailType}...`}
            </h2>
          </div>
          {/* Always show X button */}
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 p-1">
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">Progress</span>
              <span className="font-semibold text-purple-700">{percentage}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all duration-300 ${
                  isCompleted ? 'bg-green-500' : 'bg-purple-600'
                }`}
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>

          {/* Status Message */}
          <div className="text-center mb-4">
            <p className="text-gray-600">{progress.message}</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-800">{progress.total}</p>
              <p className="text-xs text-gray-500">Total</p>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{progress.sent}</p>
              <p className="text-xs text-gray-500">Sent</p>
            </div>
            <div className="text-center p-3 bg-red-50 rounded-lg">
              <p className="text-2xl font-bold text-red-600">{progress.failed}</p>
              <p className="text-xs text-gray-500">Failed</p>
            </div>
          </div>

          {/* Remaining */}
          {!isCompleted && (
            <div className="text-center text-sm text-gray-500">
              <p>Remaining: {progress.total - progress.sent - progress.failed} email(s)</p>
            </div>
          )}

          {/* Success/Failure Summary */}
          {isCompleted && (
            <div className={`p-4 rounded-lg ${progress.failed > 0 ? 'bg-yellow-50' : 'bg-green-50'}`}>
              <div className="flex items-center gap-2">
                {progress.failed > 0 ? (
                  <AlertCircle className="text-yellow-600" size={20} />
                ) : (
                  <Check className="text-green-600" size={20} />
                )}
                <p className="text-sm">
                  {progress.failed > 0 
                    ? `Completed with ${progress.failed} failure(s). ${progress.sent} email(s) sent successfully.`
                    : `All ${progress.sent} email(s) sent successfully!`
                  }
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer - Always show close button */}
        <div className="p-4 border-t bg-gray-50">
          <Button 
            onClick={onClose} 
            className="w-full"
            style={{ backgroundColor: isCompleted ? '#22c55e' : '#7c3aed' }}
          >
            {isCompleted ? 'Close' : 'Close (Sending will continue in background)'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SendingProgressModal;
